import React, { useContext, useEffect, useMemo, useState } from "react";
import { useImmer } from "use-immer";
import { DrawerHistoryContext } from "../contexts";
import { AvailabilityWithEditedFlags, isDeleted } from "../utils/editUtils";
import {
  Plan2PlateDrawer,
  Plan2PlateDrawerHeader,
  usePlan2PlateDisclosure,
} from "./Plan2PlateDrawer";
import { Department, getDepartmentIcon } from "../data/department";
import { plan2plateIcons } from "../utils/iconUtils";
import { Checkbox } from "./ui/Checkbox";
import { Input } from "./ui/Input";
import { Button } from "./ui/Button";
import { Icon } from "./ui/Icon";
import { useToast } from "./ui/Toast";
import { showValidationMessage } from "./showValidationMessage";
import { saveShops, ShopDto } from "../api";
import { queryKeys, useDepartments, useShops } from "../queries/queries";
import { useQueryClient } from "@tanstack/react-query";
import { sortBy } from "lodash";
import { Glyph } from "./SmallIconButton";

export interface AvailabilitityDrawerProps {
  availabilities: AvailabilityWithEditedFlags[] | undefined;
  purchaseId: number | undefined;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (availabilities: AvailabilityWithEditedFlags[]) => Promise<boolean>;
  submitIsSave: boolean;
}

type ShopDepartmentEntry = {
  shopId: number;
  department?: Department;
};

export const useAvailabilityDrawer = (
  onSubmit: (availabilities: AvailabilityWithEditedFlags[]) => Promise<boolean>,
  submitIsSave: boolean,
): AvailabilitityDrawerProps & {
  editAvailabilities: (
    purchaseUnitId: number,
    availabilities: AvailabilityWithEditedFlags[],
  ) => void;
} => {
  const [availabilities, updateAvailabilities] = useImmer<
    AvailabilityWithEditedFlags[] | undefined
  >(undefined);
  const [purchaseId, updatePurchaseId] = useImmer<number | undefined>(
    undefined,
  );

  const drawerHistoryContext = useContext(DrawerHistoryContext);
  const { onOpen, onClose, isOpen } =
    usePlan2PlateDisclosure(drawerHistoryContext);
  const editAvailabilities = (
    purchaseId: number,
    availabilities: AvailabilityWithEditedFlags[],
  ) => {
    updateAvailabilities(availabilities);
    updatePurchaseId(purchaseId);
    onOpen();
  };

  return {
    editAvailabilities,
    purchaseId,
    availabilities,
    isOpen,
    onClose,
    onSubmit,
    submitIsSave,
  };
};

export const AvailabilityDrawer = (props: AvailabilitityDrawerProps) => (
  <Plan2PlateDrawer
    onClose={props.onClose}
    open={props.isOpen}
    header={<Plan2PlateDrawerHeader heading="Verfügbarkeit" />}
    body={<AvailabilityDrawerBody {...props} />}
  />
);

function AvailabilityDrawerBody(props: AvailabilitityDrawerProps) {
  const shops = useShops();
  const departments = useDepartments();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [entries, setEntries] = useState<ShopDepartmentEntry[]>([]);
  const [expandedKey, setExpandedKey] = useState<string | undefined>(undefined);
  const [newShopName, setNewShopName] = useState("");

  const allShops = useMemo(() => shops, [shops]);
  const groceryShops = useMemo(
    () => allShops.filter((s) => !s.online).map((s) => s.id),
    [allShops],
  );

  // Sync initial props on open
  useEffect(() => {
    if (props.isOpen && props.availabilities) {
      const active = props.availabilities.filter(
        (a) => !isDeleted(a) && a.shopId !== -1,
      );
      setEntries(
        active.map((a) => ({ shopId: a.shopId, department: a.department })),
      );
    } else {
      setEntries([]);
    }
    setExpandedKey(undefined);
  }, [props.isOpen, props.availabilities]);

  const uniformDepartment = useMemo(() => {
    if (entries.length === 0) return undefined;
    const first = entries[0].department;
    if (!first) return undefined;
    return entries.every((e) => e.department?.name === first.name)
      ? first
      : undefined;
  }, [entries]);

  if (!props.isOpen) return null;

  const shopName = (shopId: number) =>
    shops.find((s) => s.id === shopId)?.description ?? "";
  const shopOnline = (shopId: number) =>
    shops.find((s) => s.id === shopId)?.online ?? false;

  const lastDepartment = (list: ShopDepartmentEntry[]) =>
    [...list].reverse().find((e) => e.department)?.department;

  const handleToggleShop = (shopId: number) => {
    setEntries((prev) => {
      if (prev.some((e) => e.shopId === shopId)) {
        return prev.filter((e) => e.shopId !== shopId);
      }
      return [...prev, { shopId, department: lastDepartment(prev) }];
    });
  };

  const handleToggleAllSupermarkets = (checked: boolean) => {
    setEntries((prev) => {
      if (checked) {
        const existing = new Set(prev.map((e) => e.shopId));
        const inherited = lastDepartment(prev);
        const added = groceryShops
          .filter((id) => !existing.has(id))
          .map((id) => ({ shopId: id, department: inherited }));
        return [...prev, ...added];
      }
      return prev.filter((e) => shops.find((s) => s.id === e.shopId)?.online);
    });
  };

  const handleAddCustomShop = async () => {
    const trimmed = newShopName.trim();
    if (!trimmed) return;

    if (
      allShops.some(
        (s) => s.description.toLowerCase() === trimmed.toLowerCase(),
      )
    ) {
      showValidationMessage(toast, "Dieses Geschäft existiert bereits.");
      return;
    }

    try {
      const newShop: ShopDto = {
        id: -1,
        description: trimmed,
        priority: 1,
        online: false,
      };
      const response = await saveShops({ body: [newShop] });
      await queryClient.invalidateQueries({ queryKey: queryKeys.shops });
      const created = response.data.find(
        (s) => s.description.toLowerCase() === trimmed.toLowerCase(),
      );
      if (created) {
        setEntries((prev) => [
          ...prev,
          { shopId: created.id, department: lastDepartment(prev) },
        ]);
      }
      setNewShopName("");
    } catch (e) {
      showValidationMessage(toast, "Fehler beim Hinzufügen des Geschäfts.");
    }
  };

  const handleSetDepartment = (shopId: number, department: Department) => {
    setEntries((prev) =>
      prev.map((e) => (e.shopId === shopId ? { ...e, department } : e)),
    );
    setExpandedKey(undefined);
  };

  const handleApplyAll = (department: Department) => {
    setEntries((prev) => prev.map((e) => ({ ...e, department })));
    setExpandedKey(undefined);
  };

  const handleSave = async () => {
    if (entries.length === 0) {
      showValidationMessage(toast, "Keine Geschäfte ausgewählt.");
      return;
    }
    const missing = entries
      .filter((e) => !e.department)
      .map((e) => shopName(e.shopId));
    if (missing.length > 0) {
      showValidationMessage(
        toast,
        `Keine Abteilung für: ${missing.join(", ")}`,
      );
      return;
    }

    const departmentByShop = new Map<number, Department | undefined>(
      entries.map((e) => [e.shopId, e.department]),
    );
    const updatedAvailabilities: AvailabilityWithEditedFlags[] = [];
    const originalMap = new Map<number, AvailabilityWithEditedFlags>();
    (props.availabilities || []).forEach((a) => originalMap.set(a.shopId, a));

    // Keep existing rows for still-selected shops (with their own department)
    (props.availabilities || []).forEach((a) => {
      if (departmentByShop.has(a.shopId)) {
        updatedAvailabilities.push({
          ...a,
          department: departmentByShop.get(a.shopId),
          deleted: false,
        });
      } else {
        if (a.id !== -1) {
          updatedAvailabilities.push({
            ...a,
            deleted: true,
          });
        }
      }
    });

    // Handle newly added items
    departmentByShop.forEach((department, shopId) => {
      if (!originalMap.has(shopId)) {
        updatedAvailabilities.push({
          id: -1,
          shopId,
          purchaseId: props.purchaseId || -1,
          department,
          deleted: false,
        });
      }
    });

    const success = await props.onSubmit(updatedAvailabilities);
    if (success || !props.submitIsSave) {
      props.onClose();
    }
  };

  const isAllSupermarketsChecked =
    groceryShops.length > 0 &&
    groceryShops.every((id) => entries.some((e) => e.shopId === id));

  const sortedShops = sortBy(allShops, (s) => s.description.toLowerCase());
  const sortedEntries = sortBy(entries, (e) =>
    shopName(e.shopId).toLowerCase(),
  );

  const renderDepartmentGrid = (
    selected: Department | undefined,
    onSelect: (department: Department) => void,
  ) => (
    <div className="grid grid-cols-4 gap-2 p-3 border-t border-line/60 bg-surface/20">
      {sortBy(departments, (d) => d.ordinal)
        .filter((d) => d.name !== "ONLINE")
        .map((dept) => {
          const isSelected = selected?.name === dept.name;
          const iconKey = getDepartmentIcon(dept.name);

          // Split department description to wrap lines neatly
          const lines = dept.description.split("/");

          return (
            <button
              key={dept.name}
              type="button"
              onClick={() => onSelect(dept)}
              className={`flex flex-col items-center justify-center p-1 rounded-card border text-center h-20 cursor-pointer transition select-none ${
                isSelected
                  ? "border-teal bg-teal/5 text-teal ring-1 ring-teal"
                  : "border-line bg-surface-card text-ink-muted"
              }`}
            >
              <Icon
                size={24}
                tone={isSelected ? "teal" : "default"}
                className="mb-1"
              >
                {plan2plateIcons[iconKey]}
              </Icon>
              <span className="text-2xs leading-tight font-medium max-w-full truncate-lines">
                {lines.map((line, idx) => (
                  <span key={idx} className="block">
                    {line}
                  </span>
                ))}
              </span>
            </button>
          );
        })}
    </div>
  );

  return (
    <div className="flex flex-col gap-6">
      {/* Supermärkte Section */}
      <div className="flex flex-col gap-3">
        <label className="flex cursor-pointer items-center gap-3 rounded-card border border-line bg-surface p-2.5">
          <Checkbox
            checked={isAllSupermarketsChecked}
            onCheckedChange={handleToggleAllSupermarkets}
          />
          <span className="text-sm font-medium text-ink">Alle Supermärkte</span>
        </label>

        {/* Shop Pills Grid */}
        <div className="flex flex-wrap gap-2 mt-1">
          {sortedShops.map((shop) => {
            const isSelected = entries.some((e) => e.shopId === shop.id);
            return (
              <button
                key={shop.id}
                type="button"
                onClick={() => handleToggleShop(shop.id)}
                className={`px-4 py-2 rounded-full border text-sm font-semibold transition cursor-pointer select-none ${
                  isSelected
                    ? "border-gold bg-gold/10 text-gold"
                    : "border-line bg-surface-card text-ink-muted hover:bg-surface"
                }`}
              >
                {shop.description}
              </button>
            );
          })}
        </div>
      </div>

      {/* Abteilungen Section – eine Abteilung pro Geschäft */}
      <div className="flex flex-col gap-3">
        {sortedEntries.length === 0 ? (
          <span className="text-sm text-ink-muted italic">
            Erst Geschäfte auswählen.
          </span>
        ) : (
          <div className="rounded-card border border-line bg-surface-card divide-y divide-line overflow-hidden">
            {/* Batch row: apply one department to all shops */}
            <div>
              <div className="bg-surface">
                <button
                  type="button"
                  onClick={() =>
                    setExpandedKey(expandedKey === "all" ? undefined : "all")
                  }
                  className="flex w-full items-center justify-between gap-2 p-3 text-left cursor-pointer select-none transition"
                >
                  <span className="flex items-center gap-2 text-sm font-bold text-ink">
                    Für alle festlegen
                  </span>
                  <span className="flex items-center gap-1 text-xs font-semibold text-ink-muted">
                    {uniformDepartment
                      ? uniformDepartment.description
                      : "Abteilung wählen"}
                    <span className="text-2xs">
                      {expandedKey === "all" ? "▴" : "▾"}
                    </span>
                  </span>
                </button>
              </div>
              {expandedKey === "all" &&
                renderDepartmentGrid(uniformDepartment, handleApplyAll)}
            </div>

            {/* Per-shop rows */}
            {sortedEntries.map((entry) => {
              const expanded = expandedKey === String(entry.shopId);
              return (
                <div key={entry.shopId}>
                  <button
                    type="button"
                    onClick={() =>
                      setExpandedKey(
                        expanded ? undefined : String(entry.shopId),
                      )
                    }
                    className="flex w-full items-center justify-between gap-2 p-3 text-left cursor-pointer select-none transition"
                  >
                    <span className="flex items-center gap-2 text-sm text-ink min-w-0">
                      <Glyph
                        size={22}
                        icon={shopOnline(entry.shopId) ? "atEmail" : "store"}
                      />
                      <span className="truncate">{shopName(entry.shopId)}</span>
                    </span>
                    <span className="flex items-center gap-1.5 text-xs shrink-0">
                      {entry.department ? (
                        <>
                          <Icon size={22} className="shrink-0">
                            {
                              plan2plateIcons[
                                getDepartmentIcon(entry.department.name)
                              ]
                            }
                          </Icon>
                          <span className="max-w-30 truncate">
                            {entry.department.description}
                          </span>
                        </>
                      ) : (
                        <span className="text-gold">Bitte wählen</span>
                      )}
                      <span className="text-2xs text-ink-muted">
                        {expanded ? "▴" : "▾"}
                      </span>
                    </span>
                  </button>
                  {expanded &&
                    renderDepartmentGrid(entry.department, (dept) =>
                      handleSetDepartment(entry.shopId, dept),
                    )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      <Button
        fullWidth
        variant="primary"
        size="lg"
        onClick={handleSave}
        className="bg-teal hover:brightness-110 text-white font-bold text-base flex items-center justify-center gap-2"
      >
        ✓ Verfügbarkeit speichern
      </Button>
    </div>
  );
}
